import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { Subscription } from 'rxjs';

import { Pagination } from './classes/pagination';

import { IPaginationConfig } from './interfaces/pagination-config.interface';
import { IPaginationEvent } from './interfaces/pagination-event.interface';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss']
})
export class PaginationComponent implements OnInit, OnDestroy {
  private configChangedSubscription: Subscription;

  public readonly defaultPage: number;
  public readonly defaultPagesChunk: number;
  public readonly defaultMaxPagesPerChunk: number;

  public lastPage: number;
  public currentPage: number;
  public pagesRange: number[];
  public hasNextPagesChunk: boolean;

  @Input() public config: IPaginationConfig;

  @Output() public pagesChunkChanged: EventEmitter<IPaginationEvent>;

  @Output() public pageChanged: EventEmitter<IPaginationEvent>;

  constructor() {
    this.defaultPage = Pagination.defaultPage;
    this.defaultPagesChunk = Pagination.defaultPagesChunk;
    this.defaultMaxPagesPerChunk = Pagination.defaultMaxPagesPerChunk;

    this.currentPage = Pagination.initCurrentPage();

    this.pageChanged = new EventEmitter();
    this.pagesChunkChanged = new EventEmitter();
  }

  private createPagesRange(initialPage: number): number[] | null {
    if (
      !this.config.totalItems ||
      !this.config.itemsPerPage ||
      !this.config.pagesChunk
    ) {
      return null;
    }

    let pagesRange = [];

    const numberOfPages = Pagination.calculateNumberOfPages(this.config.itemsPerPage, this.config.totalItems);
    const maxPageNumber = Pagination.calculateMaxPageNumber(this.config.pagesChunk);
    const minPageNumber = Pagination.calculateMinPageNumber(maxPageNumber);

    if (numberOfPages > Pagination.defaultMaxPagesPerChunk) {
      this.lastPage = maxPageNumber;
      this.hasNextPagesChunk = true;

      pagesRange = Pagination.createPagesRange(minPageNumber, maxPageNumber);
    } else {
      const middlePageNumber = minPageNumber + numberOfPages;

      this.lastPage = middlePageNumber;
      this.hasNextPagesChunk = false;

      pagesRange = Pagination.createPagesRange(minPageNumber, middlePageNumber);
    }

    this.currentPage = initialPage;

    return pagesRange;
  }

  ngOnInit(): void {
    if (!this.config) {
      this.config = {
        totalItems: null,
        itemsPerPage: null,
        pagesChunk: null,
        configChanged: new EventEmitter()
      };
    }

    this.pagesRange = this.createPagesRange(Pagination.defaultPage);

    this.configChangedSubscription = this.config.configChanged.subscribe(data => {
      if (!data.reset) {
        this.pagesRange = this.createPagesRange(data.initialPage);
      } else {
        this.config.totalItems = 0;
        this.config.pagesChunk = Pagination.defaultPagesChunk;

        this.pagesRange = [Pagination.defaultPage];
        this.hasNextPagesChunk = false;

        this.currentPage =
          this.lastPage = Pagination.defaultPage;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.configChangedSubscription) {
      this.configChangedSubscription.unsubscribe();
    }
  }

  getPrevious(): void {
    if (this.currentPage !== 1) {
      const page = Pagination.calculatePageWhenPreviousClicked(this.currentPage);
      const minPageNumber = Pagination.calculateMinPageNumberWhenPreviousClicked(this.config.pagesChunk);

      if (page > minPageNumber) {
        this.changePage(page);
      } else {
        this.getChunk(Pagination.calculateChunkWhenPreviousClicked(this.config.pagesChunk), false);
      }
    }
  }

  changePage(page: number): void {
    this.currentPage = page;

    const start = Pagination.calculateStartPagesChunkIndex(page, this.config.itemsPerPage, this.config.pagesChunk);
    const end = Pagination.calculateEndPagesChunkIndex(page, this.config.itemsPerPage, this.config.pagesChunk);

    this.pageChanged.emit({ start, end });
  }

  getChunk(chunk: number, direction: boolean): void {
    const page = Pagination.calculatePageWhenPagesChunkChanged(direction, this.config.pagesChunk);
    const start = Pagination.calculateStartIndexWhenPagesChunkChanged(direction, this.config.itemsPerPage);
    const end = Pagination.calculateEndIndexWhenPagesChunkChanged(direction, this.config.itemsPerPage);

    this.pagesChunkChanged.emit({ chunk, page, start, end });
  }

  getNext(): void {
    const page = Pagination.calculatePageWhenNextClicked(this.currentPage);
    const maxPageNumber = Pagination.calculateMaxPageNumberWhenNextClicked(this.config.pagesChunk);

    if (page <= maxPageNumber) {
      this.changePage(page);
    } else {
      if (this.isNextEnabled()) {
        this.getChunk(Pagination.calculateChunkWhenNextClicked(this.config.pagesChunk), true);
      }
    }
  }

  isNextEnabled(): boolean {
    return (
      (this.hasNextPagesChunk && (this.currentPage <= this.lastPage)) ||
      (!this.hasNextPagesChunk && (this.currentPage < this.lastPage))
    );
  }
}
